'use client';

import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap';
import { Pie } from 'react-chartjs-2';
import rest from '../axios';
import './css/analitico.css';

ChartJS.register(ArcElement, Tooltip, Legend);

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [visits, setVisits] = useState({});
  const [logged, setLogged] = useState(0);
  const [guest, setGuest] = useState(0);
  const [newUsers, setNewUsers] = useState(0);
  const [foundCount, setFoundCount] = useState(0);
  const [notFoundCount, setNotFoundCount] = useState(0);
  const [conversionRate, setConversionRate] = useState(0);
  const [whatsappBySub, setWhatsappBySub] = useState([]);
  const [top5Sub, setTop5Sub] = useState([]);
  const [top5Cat, setTop5Cat] = useState([]);
  const [notFoundWords, setNotFoundWords] = useState([]);
  const [foundWords, setFoundWords] = useState([]);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const { data } = await rest.get('admin/analytics', {
        params: {
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          page: currentPage,
        },
      });
      setVisits(data.visits);
      setLogged(data.logged);
      setGuest(data.guest);
      setNewUsers(data.newUsers);
      setFoundCount(data.foundCount);
      setNotFoundCount(data.notFoundCount);
      setConversionRate(data.conversionRate);
      setWhatsappBySub(data.whatsappBySub);
      setTop5Sub(data.top5Sub);
      setTop5Cat(data.top5Cat);
      setNotFoundWords(data.notFoundWords);
      setFoundWords(data.foundWords);
    } catch (err) {
      console.error('Erro ao buscar dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [startDate, endDate, currentPage]);

  const renderPie = (labels: string[], data: number[]) => (
    <div
      className="pie-container"
      style={{
        position: 'relative',
        margin: '0 auto',
      }}
    >
      <Pie
        data={{
          labels,
          datasets: [{
            data,
            backgroundColor: [
              '#36A2EB',
              '#FFCE56',
              '#FF6384',
              '#4BC0C0',
              '#9966FF',
              '#FF9F40',
            ],
          }],
        }}
        options={{
          maintainAspectRatio: false,
          responsive: true,
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                boxWidth: 20,
                font: { size: 14 },
                padding: 10,
              },
            },
          },
        }}
      />
    </div>
  );


  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-4">
      {/* Filtros */}
      <Row className="mb-4">
        <Col md={12}>
          <Form
            className="row g-3"
            onSubmit={(e) => {
              e.preventDefault();
              setCurrentPage(1);
            }}
          >
            <Col md={3}>
              <Form.Label>Data Início</Form.Label>
              <Form.Control
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Label>Data Fim</Form.Label>
              <Form.Control
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button type="submit" className="w-100">
                Aplicar Filtros
              </Button>
            </Col>
          </Form>
        </Col>
      </Row>

      <h1 className="mb-4">Dashboard Administrativo</h1>

      {/* Visitas */}
      <Row className="mb-4">
        {[7, 30, 60, 90].map((days) => (
          <Col md={3} key={days}>
            <Card className="text-center">
              <Card.Header>Visitas ({days} dias)</Card.Header>
              <Card.Body>
                <h5>{visits[days] ?? 0}</h5>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Usuários */}
      <Row className="mb-4">
        <Col md={6}>
          <Card className="text-center">
            <Card.Header>Usuários Logados</Card.Header>
            <Card.Body><h5>{logged}</h5></Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="text-center">
            <Card.Header>Usuários Não Logados</Card.Header>
            <Card.Body><h5>{guest}</h5></Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Novos Usuários */}
      <Row className="mb-4">
        <Col>
          <Card className="text-center">
            <Card.Header>Novos Usuários (últimos 30 dias)</Card.Header>
            <Card.Body><h5>{newUsers}</h5></Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Métricas de Busca */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="text-center">
            <Card.Header>Palavras Encontradas</Card.Header>
            <Card.Body><h5>{foundCount}</h5></Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Header>Palavras Não Encontradas</Card.Header>
            <Card.Body><h5>{notFoundCount}</h5></Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Header>Conversão de Busca (%)</Card.Header>
            <Card.Body><h5>{conversionRate.toFixed(2)}%</h5></Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabelas de Palavras */}
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Header>Palavras Não Encontradas</Card.Header>
            <Card.Body>
              <Table bordered hover responsive>
                <thead>
                  <tr><th>Palavra</th><th>Total</th></tr>
                </thead>
                <tbody>
                  {notFoundWords.map((w, idx) => (
                    <tr key={idx}>
                      <td>{w.word}</td>
                      <td>{w.total}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
            <Card.Footer className="d-flex justify-content-between">
              <Button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>Anterior</Button>
              <span>Página {currentPage}</span>
              <Button onClick={() => setCurrentPage(p => p + 1)}>Próxima</Button>
            </Card.Footer>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <Card.Header>Palavras Encontradas</Card.Header>
            <Card.Body>
              <Table bordered hover responsive>
                <thead>
                  <tr><th>Palavra</th><th>Total</th></tr>
                </thead>
                <tbody>
                  {foundWords.map((w, idx) => (
                    <tr key={idx}>
                      <td>{w.word}</td>
                      <td>{w.total}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Gráfico WhatsApp */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header>Clique no WhatsApp por Subcategoria</Card.Header>
            <Card.Body style={{ display: 'flex', justifyContent: 'center', backgroundColor: '#fff' }}>
              {renderPie(
                whatsappBySub.map((i) => i.name ?? '—'),
                whatsappBySub.map((i) => i.total)
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Top 5 Subcategorias e Categorias */}
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Header>Top 5 Subcategorias Mais Pesquisadas</Card.Header>
            <Card.Body style={{ display: 'flex', justifyContent: 'center', backgroundColor: '#fff' }}>
              {renderPie(
                top5Sub.map((i) => i.name),
                top5Sub.map((i) => i.count),
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <Card.Header>Top 5 Categorias Mais Pesquisadas</Card.Header>
            <Card.Body style={{ display: 'flex', justifyContent: 'center', backgroundColor: '#fff' }}>
              {renderPie(
                top5Cat.map((i) => i.name),
                top5Cat.map((i) => i.count)
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;
